// T6b (Docs/AutocompleteValueSlot-Plan.md) — binding-scoped LHS/qualifier
// resolution for value-slot completion. A comparison LHS resolves ONLY through
// the statement's alias bindings — catalog tables not bound in FROM are never
// legal owners, so there is deliberately no snapshot fallback. `[]` means
// unresolved; more than one owner means the bare column is ambiguous (D5) and
// the caller decides how to rank.

import type {
  AliasBinding,
  ColumnMeta,
  CteBinding,
  DatabaseEngineId,
  TableMeta,
} from '@/core/engines';
import { inferSubqueryColumns } from '../resolver/inferSubqueryColumns';

export type ScopedOwner = {
  readonly binding: AliasBinding;
  /** Real ColumnMeta for catalog-backed bindings; bare-name form (coarse
   *  unknown type) for CTE/derived/subquery projections. */
  readonly column: ColumnMeta | { readonly name: string };
};

export type ResolveLhsArgs = {
  readonly lhs: { readonly parts: readonly string[]; readonly quoted: readonly boolean[] };
  readonly aliasBindings: readonly AliasBinding[];
  readonly cteBindings: readonly CteBinding[];
  readonly snapshotTables: readonly TableMeta[];
  /** Full buffer text — CTE/derived projection inference re-tokenizes
   *  bodyRange slices out of it (P2-8). */
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly fold: (s: string) => string;
};

export function resolveLhsInScope(args: ResolveLhsArgs): readonly ScopedOwner[] {
  const { lhs, aliasBindings, fold } = args;
  if (lhs.parts.length === 0) return [];
  const columnFolded = fold(lhs.parts[lhs.parts.length - 1]);

  if (lhs.parts.length === 1) {
    // Bare column — every binding whose projection provably contains it.
    const owners: ScopedOwner[] = [];
    for (const binding of aliasBindings) {
      const column = lookupProjectedColumn(binding, columnFolded, args);
      if (column) owners.push({ binding, column });
    }
    return owners;
  }

  // Qualified — the segment before the column must name a binding's alias;
  // a further leading segment must match that binding's schema.
  const qualifierFolded = fold(lhs.parts[lhs.parts.length - 2]);
  const schemaPart = lhs.parts.length >= 3 ? lhs.parts[lhs.parts.length - 3] : undefined;
  const owners: ScopedOwner[] = [];
  for (const binding of aliasBindings) {
    if (fold(binding.alias) !== qualifierFolded) continue;
    if (schemaPart !== undefined) {
      if (!binding.schema || fold(binding.schema) !== fold(schemaPart)) continue;
    }
    const column = lookupProjectedColumn(binding, columnFolded, args);
    if (column) owners.push({ binding, column });
  }
  return owners;
}

/** Resolve `columnFolded` inside one binding's projection, or undefined when
 *  the projection provably lacks it (or cannot be established). */
function lookupProjectedColumn(
  binding: AliasBinding,
  columnFolded: string,
  args: ResolveLhsArgs,
): ScopedOwner['column'] | undefined {
  const { fold } = args;
  if (binding.kind === 'table' || binding.kind === 'view') {
    // All same-named snapshot tables the binding could refer to. For an
    // unqualified binding this spans schemas — search_path is NOT modeled
    // here (C3 sol finding 3, gaps-doc'd), so instead of trusting the first
    // match we resolve through EVERY candidate: when the same-named tables
    // that carry the column agree on its declared type, the answer is safe
    // regardless of which schema wins; when they disagree, we return
    // undefined and the LHS stays 'unknown' — no demotion, mirroring the
    // D5 multiple-owners-disagree rule.
    const matches = args.snapshotTables.filter(
      (t) =>
        fold(t.name) === fold(binding.relation) &&
        (binding.schema === undefined ||
          t.schema === undefined ||
          fold(t.schema) === fold(binding.schema)),
    );
    if (matches.length === 0) return undefined;
    const havers = matches
      .map((t) => t.columns.find((c) => fold(c.name) === columnFolded))
      .filter((c) => c !== undefined);
    if (havers.length === 0) return undefined;
    const types = new Set(havers.map((c) => (c!.dataType ?? '').toLowerCase()));
    return types.size === 1 ? havers[0] : undefined;
  }

  if (binding.kind === 'cte') {
    const cte = args.cteBindings.find((c) => fold(c.name) === fold(binding.relation));
    if (!cte) return undefined;
    const names =
      cte.columns ??
      (cte.bodyRange ? inferProjection(cte.bodyRange, args) : undefined);
    return matchName(names, columnFolded, fold);
  }

  // subquery / derived — writer-supplied TVF column list wins, else infer
  // from the body.
  const names =
    binding.syntheticColumns ??
    (binding.bodyRange ? inferProjection(binding.bodyRange, args) : undefined);
  return matchName(names, columnFolded, fold);
}

function inferProjection(
  bodyRange: { readonly start: number; readonly end: number },
  args: ResolveLhsArgs,
): readonly string[] {
  return inferSubqueryColumns({
    source: args.source,
    bodyRange,
    engineId: args.engineId,
    snapshotTables: args.snapshotTables,
    cteBindings: args.cteBindings,
  });
}

function matchName(
  names: readonly string[] | undefined,
  columnFolded: string,
  fold: (s: string) => string,
): { readonly name: string } | undefined {
  const hit = names?.find((n) => fold(n) === columnFolded);
  return hit !== undefined ? { name: hit } : undefined;
}
